import React, { useEffect, useState } from 'react';
import V4Tabs from './V4Tabs';
import { API_BASE_URL } from '../../config';
import { useAuth } from '../../contexts/AuthContext';

const DocumentsSettingsV4 = () => {
  const { getAuthHeaders } = useAuth();
  const [embConfig, setEmbConfig] = useState({ api_key: '', model: 'text-embedding-3-small' });
  const [v4Settings, setV4Settings] = useState({
    cache_followups: true,
    tts_provider: 'browser',
    tts_voice: 'en-US-Neural2-C',
    tts_rate: 1.0,
    summary_top_k: 6,
    summary_bottom_k: 0,
    debug_include_vectors: false,
    embed_parallel_threads: 2,
    embed_batch_size: 32,
    // New: Completions model and Pinecone config for v4
    response_model: 'gpt-4',
    query_expand_system_prompt: '',
    v4_log_level: 'INFO',
    pinecone: { api_key: '', host: '', index: '' },
  });
  const [prompts, setPrompts] = useState({ assistant_system_prompt: '' });
  const [embedPrompt, setEmbedPrompt] = useState('');
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/v4/documents/config/embeddings`, { headers: { ...getAuthHeaders() } });
        if (res.ok) {
          const data = await res.json();
          setEmbConfig({ api_key: data?.config?.api_key || '', model: data?.config?.model || 'text-embedding-3-small' });
        }
      } catch {}
      try {
        const r2 = await fetch(`${API_BASE_URL}/api/v4/documents/config/settings`, { headers: { ...getAuthHeaders() } });
        if (r2.ok) {
          const d2 = await r2.json();
          setV4Settings({
            cache_followups: !!(d2?.config?.cache_followups ?? true),
            tts_provider: d2?.config?.tts_provider || 'browser',
            tts_voice: d2?.config?.tts_voice || 'en-US-Neural2-C',
            tts_rate: Number(d2?.config?.tts_rate ?? 1.0),
            summary_top_k: Number(d2?.config?.summary_top_k ?? 6),
            summary_bottom_k: Number(d2?.config?.summary_bottom_k ?? 0),
            debug_include_vectors: !!(d2?.config?.debug_include_vectors ?? false),
            embed_parallel_threads: Number(d2?.config?.embed_parallel_threads ?? 2),
            embed_batch_size: Number(d2?.config?.embed_batch_size ?? 32),
            response_model: d2?.config?.response_model || 'gpt-4',
            query_expand_system_prompt: d2?.config?.query_expand_system_prompt || '',
            v4_log_level: (d2?.config?.v4_log_level || 'INFO'),
            pinecone: {
              api_key: d2?.config?.pinecone?.api_key || '',
              host: d2?.config?.pinecone?.host || d2?.config?.pinecone?.environment || '',
              index: d2?.config?.pinecone?.index || '',
            },
          });
        }
      } catch {}
      try {
        const rp = await fetch(`${API_BASE_URL}/api/v4/documents/config/prompts`, { headers: { ...getAuthHeaders() } });
        if (rp.ok) {
          const dp = await rp.json();
          const p = dp?.prompts || {};
          setPrompts({
            assistant_system_prompt: p.assistant_system_prompt || '',
          });
          setEmbedPrompt(p.embedding_preprocess_prompt || '');
        }
      } catch {}
    })();
  }, [getAuthHeaders]);

  const save = async () => {
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/config/embeddings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(embConfig),
    });
    alert(res.ok ? 'Saved' : 'Save failed');
  };

  const saveV4Settings = async () => {
    const r = await fetch(`${API_BASE_URL}/api/v4/documents/config/settings`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(v4Settings),
    });
    alert(r.ok ? 'Settings saved' : 'Save failed');
  };

  const clearAssistantCache = async () => {
    const res = await fetch(`${API_BASE_URL}/api/v4/documents/assistant/cache/clear`, {
      method: 'POST',
      headers: { ...getAuthHeaders() },
    });
    alert(res.ok ? 'Assistant cache cleared' : 'Failed to clear cache');
  };

  return (
    <div className="container py-4">
      <h3 className="mb-3">Documents v4</h3>
      <V4Tabs />
      <div className="card mb-3">
        <div className="card-body">
          <h6 className="mb-3">Embeddings (v4)</h6>
          <div className="row g-3">
            <div className="col-md-8">
              <label className="form-label">OpenAI API Key</label>
              <input type="password" className="form-control" value={embConfig.api_key} onChange={(e) => setEmbConfig({ ...embConfig, api_key: e.target.value })} placeholder="Enter key" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Model</label>
              <input className="form-control" value={embConfig.model} onChange={(e) => setEmbConfig({ ...embConfig, model: e.target.value })} placeholder="text-embedding-3-small" />
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={save}>Save</button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h6 className="mb-3">Assistant Settings</h6>
          <div className="form-check form-switch mb-3">
            <input className="form-check-input" type="checkbox" id="cacheFollowups" checked={v4Settings.cache_followups} onChange={(e) => setV4Settings({ ...v4Settings, cache_followups: e.target.checked })} />
            <label className="form-check-label" htmlFor="cacheFollowups">Cache and use context for follow-up questions</label>
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Assistant Log Level</label>
              <select className="form-select" value={v4Settings.v4_log_level} onChange={(e) => setV4Settings({ ...v4Settings, v4_log_level: e.target.value })}>
                <option value="DEBUG">DEBUG</option>
                <option value="INFO">INFO</option>
                <option value="WARNING">WARNING</option>
                <option value="ERROR">ERROR</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
              <small className="text-muted">Controls logging to backend/logs/v4-ai-assistant.log</small>
            </div>
            <div className="col-md-4">
              <label className="form-label">TTS Provider</label>
              <select className="form-select" value={v4Settings.tts_provider} onChange={(e) => setV4Settings({ ...v4Settings, tts_provider: e.target.value })}>
                <option value="browser">Browser (SpeechSynthesis)</option>
                <option value="google">Google Cloud TTS</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Voice (Google)</label>
              <input className="form-control" value={v4Settings.tts_voice} onChange={(e) => setV4Settings({ ...v4Settings, tts_voice: e.target.value })} placeholder="en-US-Neural2-C" />
            </div>
            <div className="col-md-4">
              <label className="form-label">Rate</label>
              <input type="number" step="0.1" min="0.5" max="2" className="form-control" value={v4Settings.tts_rate} onChange={(e) => setV4Settings({ ...v4Settings, tts_rate: Number(e.target.value) })} />
            </div>
            <div className="col-md-4">
              <label className="form-label">Completions Model</label>
              <input className="form-control" value={v4Settings.response_model}
                onChange={(e) => setV4Settings({ ...v4Settings, response_model: e.target.value })}
                placeholder="gpt-4o-mini" />
              <small className="text-muted">Model used for responses (chat completions)</small>
            </div>
            <div className="col-12">
              <label className="form-label">Query Expansion System Prompt</label>
              <textarea className="form-control" rows={5}
                placeholder="Define how the model should expand user queries into concise variants"
                value={v4Settings.query_expand_system_prompt}
                onChange={(e) => setV4Settings({ ...v4Settings, query_expand_system_prompt: e.target.value })}
              />
              <small className="text-muted">Used to generate synonym/variant queries for vector search pooling.</small>
            </div>
            <div className="col-md-4">
              <label className="form-label">Context: Top chunks</label>
              <input type="number" min="0" max="50" className="form-control" value={v4Settings.summary_top_k} onChange={(e) => setV4Settings({ ...v4Settings, summary_top_k: Math.max(0, Number(e.target.value)) })} />
              <small className="text-muted">How many highest-scoring chunks to include first</small>
            </div>
            <div className="col-md-4">
              <label className="form-label">Context: Bottom chunks</label>
              <input type="number" min="0" max="50" className="form-control" value={v4Settings.summary_bottom_k} onChange={(e) => setV4Settings({ ...v4Settings, summary_bottom_k: Math.max(0, Number(e.target.value)) })} />
              <small className="text-muted">How many lowest-scoring chunks to include last</small>
            </div>
            <div className="col-md-4 d-flex align-items-end">
              <div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="debugVectors"
                    checked={v4Settings.debug_include_vectors}
                    onChange={(e) => setV4Settings({ ...v4Settings, debug_include_vectors: e.target.checked })} />
                  <label className="form-check-label" htmlFor="debugVectors">Include raw vectors in debug logs</label>
                </div>
                <small className="text-muted">When off, vectors are masked in debug payloads.</small>
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Embedding Parallel Threads</label>
              <input type="number" min="1" max="16" className="form-control"
                value={v4Settings.embed_parallel_threads}
                onChange={(e) => setV4Settings({ ...v4Settings, embed_parallel_threads: Math.max(1, Number(e.target.value)) })}
              />
              <small className="text-muted">Number of parallel threads for batch embeddings</small>
            </div>
            <div className="col-md-4">
              <label className="form-label">Embedding Batch Size</label>
              <input type="number" min="1" max="128" className="form-control"
                value={v4Settings.embed_batch_size}
                onChange={(e) => setV4Settings({ ...v4Settings, embed_batch_size: Math.max(1, Number(e.target.value)) })}
              />
              <small className="text-muted">Number of chunks per API batch</small>
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={saveV4Settings}>Save Settings</button>
            <button className="btn btn-outline-danger" onClick={clearAssistantCache}>Clear Assistant Cache</button>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-body">
          <h6 className="mb-3">Pinecone (v4)</h6>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">API Key</label>
              <input type="password" className="form-control" value={v4Settings.pinecone.api_key}
                onChange={(e) => setV4Settings({ ...v4Settings, pinecone: { ...v4Settings.pinecone, api_key: e.target.value } })}
                placeholder="pc-*****" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Host</label>
              <input className="form-control" value={v4Settings.pinecone.host}
                onChange={(e) => setV4Settings({ ...v4Settings, pinecone: { ...v4Settings.pinecone, host: e.target.value } })}
                placeholder="https://your-index.svc.region.pinecone.io" />
            </div>
            <div className="col-md-6">
              <label className="form-label">Index</label>
              <input className="form-control" value={v4Settings.pinecone.index}
                onChange={(e) => setV4Settings({ ...v4Settings, pinecone: { ...v4Settings.pinecone, index: e.target.value } })}
                placeholder="ai-tech-assistant-v4" />
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={saveV4Settings}>Save Pinecone</button>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-body">
          <h6 className="mb-3">Prompts (v4)</h6>
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Assistant System Prompt</label>
              <textarea className="form-control" rows={8} value={prompts.assistant_system_prompt} onChange={(e) => setPrompts({ ...prompts, assistant_system_prompt: e.target.value })} placeholder="Define the assistant's behavior and formatting here..." />
              <small className="text-muted">Used by the v4 Assistant for both sync and streaming responses. 100% editable.</small>
            </div>
            <div className="col-12">
              <label className="form-label">Embedding Preprocess Prompt</label>
              <textarea className="form-control" rows={6} value={embedPrompt} onChange={(e) => setEmbedPrompt(e.target.value)} placeholder="Template for cleaning chunks before embedding" />
              <small className="text-muted">Applied only if enabled below. Use placeholder {'{chunk}'} to include the original text.</small>
            </div>
          </div>
          <div className="mt-3 d-flex gap-2">
            <button className="btn btn-primary" onClick={async () => {
              const r = await fetch(API_BASE_URL + '/api/v4/documents/config/prompts', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({
                  ...prompts,
                  embedding_preprocess_prompt: embedPrompt,
                }),
              });
              alert(r.ok ? 'Prompts saved' : 'Save failed');
            }}>Save Prompts</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentsSettingsV4;